import {
  EVIDENCIA_STATUS_APROVADA,
  EVIDENCIA_STATUS_REPROVADA,
  axiosUploadProgress,
  fetchAllPages,
  newFormData,
} from 'js/utils/GLOBAL'
import { Dialog } from 'quasar'
import { evidenciaService } from 'services/evidencia.service'
import { storeToRefs } from 'pinia'
import { useEvidenciaStore } from 'store/evidencia.store'
import { useI18n } from 'vue-i18n'
import ModalSweetAlert from 'components/Modal/ModalSweetAlert.vue'
import useApiError from './useApiError'
import usePerguntas from './usePerguntas'

export function useEvidencia() {
  const { t } = useI18n()
  const {
    evidencias,
    evidencia,
    filesData,
    loading,
    modalEvidenciaCreateRef,
    modalEvidenciaRef,
    agrupamentoEnvio,
    agrupamentoPeriodo,
  } = storeToRefs(useEvidenciaStore())

  const {
    getEvidencia,
    postEvidencia,
    postEvidenciaFile,
    postStatusEvidencia,
    patchEvidencia,
    getAgrupamentoEnvio,
    getAgrupamentoPeriodo,
  } = evidenciaService()

  const { initialRequests, pergunta, tipoEvidencia } = usePerguntas()

  async function initialRequestsEvidencia(idEvidencia) {
    loading.value.getEvidencia = true
    try {
      const req = await getEvidencia(idEvidencia)
      evidencia.value = req
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.getEvidencia = false
    }
  }

  async function getAnteriores(evidencia) {
    try {
      // const req = await getAgrupamentoEnvio({
      //   evidencia: evidencia.id,
      // })
      loading.value.getAnteriores = true
      const data = await fetchAllPages({
        getFun: getAgrupamentoEnvio,
        prop: agrupamentoEnvio,
        params: {
          evidencia: evidencia,
        },
      })
      return data
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.getAnteriores = false
    }
  }

  async function getAnterioresPeriodo(evidencia) {
    try {
      loading.value.getAnteriores = true
      const data = await getAgrupamentoPeriodo(evidencia)
      return data
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.getAnteriores = false
    }
  }

  async function sendEvidencia(model) {
    try {
      loading.value.evidencia.enviando = true

      const response = await postEvidencia({
        pergunta: tipoEvidencia.value.pergunta
          ? model.pergunta
          : null,
        alternativas: model.alternativas,
        comentario: model.comentario,
        fabricante: window.context.user.fabricante_id,
      })

      const evidencias = Array.isArray(response)
        ? response
        : [response]

      const uploadPromises = evidencias.map((evidencia) => {
        const formData = newFormData({
          evidencia: evidencia.evidencia_id,
          arquivo: model.files,
        })

        return postEvidenciaFile(formData, {
          ...axiosUploadProgress(loading.value.evidencia.porcentagem),
        })
      })

      await Promise.all(uploadPromises)
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.evidencia.enviando = false
    }
  }

  async function onSaveEvidencia(model) {
    try {
      await sendEvidencia(model)
      modalEvidenciaCreateRef.value.close({})
      showModalConfirm({ text: t('evidenceSentSuccessfully') })
      await initialRequests(pergunta.value.id)
    } catch (e) {
      useApiError(e)
    }
  }

  async function showModalConfirm(props) {
    Dialog.create({
      component: ModalSweetAlert,
      componentProps: {
        type: 'sucess',
        timeout: 2500,
        ...props,
      },
    })
  }
  async function onClickAprovarReprovar(param, aprovar) {
    const evidencia = param.evidencia
    const pergunta = evidencia.pergunta
    const alternativas = evidencia.alternativas

    Dialog.create({
      title: aprovar ? t('approveEvidence') : t('reproveEvidence'),
      message: t(
        aprovar
          ? 'messageConfirmSendEvidence'
          : 'reproveEvidenceMessage',
        {
          pergunta: pergunta.pergunta,
          alternativas: alternativas.map((a) => a.titulo).join(', '),
        }
      ),
      ok: {
        label: t('confirm'),
        primary: true,
        class: `min-h-42 !min-w-120 ${aprovar ? '!bg-alert-success' : '!bg-alert-error'}`,
      },
      cancel: {
        secondary: true,
        class: 'min-h-42 !bg-white  !min-w-120',
      },
      persistent: true,
    }).onOk(async (payload) => {
      const status = aprovar
        ? EVIDENCIA_STATUS_APROVADA
        : EVIDENCIA_STATUS_REPROVADA
      changeStatusEvidencia(param, status)
    })
  }

  async function changeStatusEvidencia(param, status) {
    const { evidencia, material_evidencia, comentario_supervisor } =
      param
    await postStatusEvidencia({
      id_evidencia: tipoEvidencia.value.alternativas
        ? evidencia.id
        : null,
      pergunta: tipoEvidencia.value.pergunta
        ? evidencia.pergunta.id
        : null,
      material_evidencia,
      comentario_supervisor,
      status,
    })

    showModalConfirm({ text: t('stateEvidence') })
    initialRequests(evidencia.pergunta.id)
    modalEvidenciaRef.value.close()
  }

  async function onClickEditar({ evidencia, files, comentario }) {
    try {
      loading.value.evidencia.enviando = true
      await patchEvidencia(evidencia.id, {
        comentario: comentario,
        pergunta: tipoEvidencia.value.pergunta
          ? evidencia.pergunta.id
          : null,
      })

      if (files.length) {
        const formData = newFormData({
          evidencia: evidencia.id,
          arquivo: files,
        })

        await postEvidenciaFile(formData, {
          ...axiosUploadProgress(loading.value.evidencia.porcentagem),
        })
      }

      modalEvidenciaRef.value.close()
      showModalConfirm({ text: t('changeEvidence') })
      initialRequests(pergunta.value.id)
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.evidencia.enviando = false
    }
  }

  return {
    evidencias,
    initialRequestsEvidencia,
    modalEvidenciaCreateRef,
    modalEvidenciaRef,
    onSaveEvidencia,
    loading,
    onClickAprovarReprovar,
    onClickEditar,
    filesData,
    getAnteriores,
    getAnterioresPeriodo,
    agrupamentoEnvio,
    agrupamentoPeriodo,
  }
}
